import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Subscription } from 'rxjs'
import { debounceTime, tap } from 'rxjs/operators'
import { Alert, AlertTitle, Button, Card, Dialog, Grid, Slide, makeStyles, useSubject, withErrorBoundary } from '@cicero/ui'
import { LOGGER } from '@cicero/data/utils'
import { IDocumentContent, PageDoc, PageSchema, StoreContext, useModel } from '../models'
import { ContentPlugin, DashboardPlugin, SpreadsheetPlugin } from './plugins'
import { ActiveSelectionToolbelt, CommandPallette, PageDetails, PageEditorToolbar } from './tools'
import { EditorContext, EditorService, SlateEditable, SlateEditor } from './EditorService'

export const Editor = ({ page, quickActions }: { page: PageDoc, quickActions?: JSX.Element }) => {
  const { editorContainer, articleContainer, editableContainer } = styles()
  const store = useContext(StoreContext)
  const editor = useMemo(() => EditorService.createEditor(), [])
  const service = useMemo(() => new EditorService(editor, [ContentPlugin, DashboardPlugin, SpreadsheetPlugin], page, { content: page.content }), [editor, page])
  const [content, content$] = useSubject<IDocumentContent[]>([], [page])
  const [showDetails, setShowDetails] = useState(false)

  // Prepare initial content, then persist content changes after a debounce
  useEffect(() => {
    let latest: any = null
    const subs: Subscription[] = []
    // Gets the raw content and prepares it for display
    const prepareContent = async () => {
      const cc = await service.preRender(page.content || PageSchema.DEFAULT_CONTENT, store)
      content$.next(cc)
      const sub = content$.pipe(tap(c => { latest = c }), debounceTime(2500)).subscribe((c) => { latest = null; persist(c) })
      subs.push(sub)
    }

    // Saves changes to content
    const persist = async (c: any) => {
      if (JSON.stringify(page.content) !== JSON.stringify(c)) {
        const rawContent = await service.preSave(c, store)
        page.atomicSet('content', rawContent)
      }
    }

    prepareContent()
    return () => {
      if (latest !== null) { persist(latest) }
      subs.map(sub => sub.unsubscribe())
    }
  }, [content$, page])

  useEffect(() => { return () => { service.destroy() } }, [service])

  return (
    <section className={editorContainer}>
      <EditorContext.Provider value={service}>
        <SlateEditor
          editor={editor}
          value={content}
          onChange={(c: any) => {
            content$.next(c)
            service.handleChange(c)
          }}
        >
          <PageEditorToolbar page={page}>
            <ul>
              { quickActions
                ? quickActions
                : <li><Button onClick={() => setShowDetails(!showDetails)}>Details</Button></li>
              }
            </ul>
          </PageEditorToolbar>
          <ActiveSelectionToolbelt />
          <CommandPallette />
          <article className={articleContainer}>
            <Grid item xs={12}>
              <SlateEditable
                className={editableContainer}
                // https://github.com/ianstormtaylor/slate/blob/master/site/examples/markdown-preview.tsx
                // decorate={([node, path]) => {
                //   console.log('dec', node, path)
                //   return []
                // }}
                renderElement={(p) => service.renderElement(p)}
                renderLeaf={(p) => service.renderLeaf(p)}
                onKeyDown={(e) => service.handleKeyPress(e)}
                placeholder="Start Typing, or type / for commands"
                spellCheck={false}
              />
            </Grid>
            <Slide direction="left" in={showDetails} mountOnEnter unmountOnExit>
              <Card><PageDetails page={page} /></Card>
            </Slide>
          </article>
        </SlateEditor>
      </EditorContext.Provider>
    </section>
  )
}

export const EditorError = ({ error }: { error?: any }) => {
  return <Alert severity="error">
    <AlertTitle>Error</AlertTitle>
    { error.message }
  </Alert>
}

export const SafeEditor = withErrorBoundary(Editor, {
  FallbackComponent: EditorError,
  onError(error, info) {
    LOGGER.error('EDITOR_ERROR', { error, info })
  },
})

export const EditorModal = ({ page }: { page: PageDoc|null }) => {
  const Pages = useModel(PageSchema)
  const history = useHistory()

  const finishEditing = (navigate = false) => {
    Pages.active$.next(null)
    if (navigate && page) { history.push(PageSchema.getPageLink(page)) }
  }

  if (!page) { return null }
  return <Dialog
    fullWidth
    maxWidth="lg"
    onClose={() => finishEditing()}
    open={page !== null}
  >
    <SafeEditor
      page={page}
      quickActions={<li>
        <Button onClick={() => finishEditing(true)}>Open Page</Button>
      </li>}
    />
  </Dialog>
}

const styles = makeStyles({
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    background: '#f8f9fa',
    minHeight: 600,
  },
  articleContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  editableContainer: {
    position: 'relative',
    margin: 12,
    flexGrow: 1,
  },
})
